import { getTaggingFlag } from '@/app/data-catalog/_common/constants'
import { toast } from '@/components/ui/toaster'
import { TaggingFlag, useSaveColumnMutation } from '@/hooks/api/use-save-column-metadata-mutation'
import { useSaveTableMetadataMutation } from '@/hooks/api/use-save-table-metadata-mutation'
import {
  ColumnDetail,
  TableDetailsQueryParams,
  TagsStructure,
} from '@/hooks/api/use-table-details-query'
import { Table, TablesQueryParams } from '@/hooks/api/use-table-query'
import React from 'react'
import { normalizeTagsStructure } from '../utils'

export type SaveContext = 'enrichment' | 'editing'

export type TableDescriptionData = {
  table_name: string
  table_description: string
}

export type ColumnData = {
  column_name: string
  column_description?: string
  business_definition?: string
  business_rules?: string
  tags?: TagsStructure
}

export type SaveTableDescriptionsParams = {
  service_name: string
  database_name: string
  schema_name: string
  tables: TableDescriptionData[]
}

export type SaveColumnDataParams = {
  service_name: string
  database_name: string
  schema_name: string
  tables: Array<{
    table_name: string
    tagging_flag?: TaggingFlag
    columns: ColumnData[]
  }>
}

interface UseSaveMetadataProps {
  queryParams: TablesQueryParams | TableDetailsQueryParams
  context: SaveContext
  onSuccess?: () => void
  selectedMetadata?: string[] // Add selected metadata to determine tagging flag
  refetchScoreQueries?: () => void // Optional refetch function for score queries
}

export const useSaveMetadata = ({
  queryParams,
  context,
  onSuccess,
  selectedMetadata,
  refetchScoreQueries,
}: UseSaveMetadataProps) => {
  const [isSaving, setIsSaving] = React.useState(false)

  const saveTableMutation = useSaveTableMetadataMutation()
  const saveColumnMutation = useSaveColumnMutation()

  // Type guard to check if queryParams has table_name (TableDetailsQueryParams)
  const isTableDetails = (
    params: TablesQueryParams | TableDetailsQueryParams,
  ): params is TableDetailsQueryParams => {
    return 'table_name' in params
  }

  // Save table descriptions
  const saveTableDescriptions = React.useCallback(
    async (tables: TableDescriptionData[]): Promise<boolean> => {
      if (!tables || tables.length === 0) {
        toast.error('No table descriptions to save')
        return false
      }

      setIsSaving(true)
      try {
        const saveParams: SaveTableDescriptionsParams = {
          service_name: queryParams.service_name,
          database_name: queryParams.database_name,
          schema_name: queryParams.schema_name,
          tables,
        }

        const response = await saveTableMutation.mutateAsync(saveParams)

        if (response) {
          const message =
            context === 'editing'
              ? 'Saved all edits and created a new version'
              : 'Table descriptions saved successfully'
          toast.success(message)
          onSuccess?.()
          refetchScoreQueries?.()
          return true
        } else {
          toast.error('Error saving table descriptions')
          return false
        }
      } catch (error) {
        console.error('Error saving table descriptions:', error)
        toast.error('Error saving table descriptions')
        return false
      } finally {
        setIsSaving(false)
      }
    },
    [queryParams, saveTableMutation, context, onSuccess, refetchScoreQueries],
  )

  // Save column data
  const saveColumnData = React.useCallback(
    async (columns: ColumnData[], tableName?: string): Promise<boolean> => {
      if (!columns || columns.length === 0) {
        toast.error('No column data to save')
        return false
      }

      setIsSaving(true)
      try {
        // Use provided tableName or extract from queryParams
        const targetTableName =
          tableName || (isTableDetails(queryParams) ? queryParams.table_name : '')

        if (!targetTableName) {
          toast.error('Table name is required for saving column data')
          return false
        }

        const taggingFlag = getTaggingFlag(selectedMetadata)

        const saveParams: SaveColumnDataParams = {
          service_name: queryParams.service_name,
          database_name: queryParams.database_name,
          schema_name: queryParams.schema_name,
          tables: [
            {
              table_name: targetTableName,
              tagging_flag: taggingFlag,
              columns,
            },
          ],
        }

        const result = await saveColumnMutation.mutateAsync(saveParams)

        if (result) {
          const message =
            context === 'editing'
              ? 'Saved all edits and created a new version'
              : 'Column metadata saved successfully'
          toast.success(message)
          onSuccess?.()
          refetchScoreQueries?.()
          return true
        } else {
          toast.error('Error saving column metadata')
          return false
        }
      } catch (error) {
        console.error('Error saving column metadata:', error)
        toast.error('Error saving column metadata')
        return false
      } finally {
        setIsSaving(false)
      }
    },
    [queryParams, saveColumnMutation, context, onSuccess, selectedMetadata, refetchScoreQueries],
  )

  // Helper function to convert table enrichment data to save format
  const convertEnrichmentTableData = React.useCallback((tables: any[]): TableDescriptionData[] => {
    return tables
      .map((table: Table) => {
        const tableDescription = table.table_description || (table as any).tableDescription || ''
        return {
          table_name: table.table_name || (table as any).tableName,
          table_description: tableDescription,
        }
      })
      .filter((table) => table.table_name) // Filter out invalid entries
  }, [])

  // Helper function to convert column enrichment data to save format
  const convertEnrichmentColumnData = React.useCallback((columns: any[]): ColumnData[] => {
    return columns.map((column: ColumnDetail) => {
      const columnName = column.column_name || (column as any).columnName || ''
      const columnDescription = column.column_description || (column as any).columnDescription || ''
      const businessDefinition =
        column.business_definition || (column as any).businessDefinition || ''
      const businessRules = column.business_rules || (column as any).businessRules || ''

      // Use centralized normalization function
      const tags = normalizeTagsStructure(column.tags)

      return {
        column_name: columnName,
        column_description: columnDescription,
        business_definition: businessDefinition,
        business_rules: businessRules,
        tags,
      }
    })
  }, [])

  // Helper function to convert editing data to save format
  const convertEditingTableData = React.useCallback(
    (tables: Record<string, any>): TableDescriptionData[] => {
      return Object.entries(tables)
        .filter(([_, data]) => data.table_description)
        .map(([tableName, data]) => ({
          table_name: tableName,
          table_description: data.table_description,
        }))
    },
    [],
  )

  // Helper function to convert editing column data to save format
  const convertEditingColumnData = React.useCallback(
    (columns: Record<string, any>, originalColumns: any[] = []): ColumnData[] => {
      const originalColumnsMap = new Map(originalColumns.map((col) => [col.column_name, col]))

      return Object.entries(columns).map(([columnName, editedData]) => {
        // Find the original column to get all original data
        const originalColumn = originalColumnsMap.get(columnName)

        if (!originalColumn) {
          console.warn(`Original column not found for ${columnName}`)
          return {
            column_name: columnName,
            column_description: editedData.column_description || '',
            business_definition: editedData.business_definition || '',
            business_rules: editedData.business_rules || '',
            tags: editedData.tags || { PII_DG: [], DATA_QUALITY_TAGS: [], SENSITIVITY_TAGS: [] },
          }
        }

        // Merge edited fields with original data
        const mergedTags: TagsStructure = {
          PII_DG: editedData.tags?.PII_DG || originalColumn.tags?.PII_DG || [],
          DATA_QUALITY_TAGS:
            editedData.tags?.DATA_QUALITY_TAGS || originalColumn.tags?.DATA_QUALITY_TAGS || [],
          SENSITIVITY_TAGS: originalColumn.tags?.SENSITIVITY_TAGS || [],
        }

        return {
          column_name: columnName,
          column_description:
            editedData.column_description !== undefined
              ? editedData.column_description
              : originalColumn.column_description || '',
          business_definition:
            editedData.business_definition !== undefined
              ? editedData.business_definition
              : originalColumn.business_definition || '',
          business_rules:
            editedData.business_rules !== undefined
              ? editedData.business_rules
              : originalColumn.business_rules || '',
          tags: mergedTags,
        }
      })
    },
    [],
  )

  // Helper function to check if business fields were edited
  const checkBusinessFieldsEdited = React.useCallback(
    (editedColumns: Record<string, any>, originalColumns: any[]): boolean => {
      const originalColumnsMap = new Map(originalColumns.map((col) => [col.column_name, col]))

      return Object.entries(editedColumns).some(([columnName, editedData]) => {
        const originalColumn = originalColumnsMap.get(columnName)
        if (!originalColumn) return false

        // Only check fields that were actually edited (exist in editedData)
        const businessDefinitionChanged =
          editedData.business_definition !== undefined &&
          editedData.business_definition !== (originalColumn.business_definition || '')

        const businessRulesChanged =
          editedData.business_rules !== undefined &&
          editedData.business_rules !== (originalColumn.business_rules || '')

        return businessDefinitionChanged || businessRulesChanged
      })
    },
    [],
  )

  return {
    isSaving,
    saveTableDescriptions,
    saveColumnData,
    convertEnrichmentTableData,
    convertEnrichmentColumnData,
    convertEditingTableData,
    convertEditingColumnData,
    checkBusinessFieldsEdited,
  }
}
